import { OmniElement, OmniStyle, OmniIcon, html, css } from 'omni-ui';
import { sleep } from './om2-util.js';

OmniStyle.register();
OmniIcon.register();

/* Light wrapper around bulma's dropdown with an icon trigger and "fixed" css
 * positioning of the dropdown area. Basically a stripped down version of
 * Om2Dropdown. Hopefully this will eventually be solved by omni-ui.
 */
export class Om2IconMenu extends OmniElement {
  constructor() {
    super();
    this.options = [];
    this.nameProperty = 'label';
    this.isActive = false;
    this.iconId = 'omni:interactive:actions';
    this.iconClass = 'is-size-2 has-text-almost-black';
    this.buttonClass = 'is-text is-shadowless has-text-core-dark';
  }

  static get properties() {
    return {
      /* This is the full list of items to show in the dropdown */
      options: { type: Array },

      /* Optional text label to add next to icon */
      label: { type: String },

      /* The property name to use as a display label (default "label") */
      nameProperty: { type: String },

      /* The name of the omni-ui icon to use for the trigger */
      iconId: { type: String },

      /* Override the class string of the omni-icon */
      iconClass: { type: String },

      /* Override the class string of the button containing the omni-icon */
      buttonClass: { type: String },

      /* The tooltip to show when hovering over trigger icon */
      tooltip: { type: String },

      /* Below are for internal use */
      isActive: { attribute: false },
      loading: { attribute: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          display: inline-block;
        }
        /* Implement "disabled" with CSS on first child */
        :host([disabled]) > * {
          position: relative;
        }
        :host([disabled]) > *::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: white;
          left: 0;
          top: 0;
          opacity: 0.4;
          cursor: not-allowed;
        }
        .omni .dropdown-menu {
          padding-bottom: 4px;
          position: fixed;
          z-index: 40; /* set above omni-ui's app-layout header, same as modals */
        }
        .omni .dropdown-content {
          overflow-x: hidden;
          overflow-y: auto;
          min-width: 163px;
          position: fixed;
        }
        .omni .dropdown-item {
          line-height: normal;
        }
        :host(:not(.is-small)) .omni .dropdown-item {
          font-size: 14px;
          line-height: 22px;
          padding: 7px 32px;
        }
        .omni .dropdown-item:hover {
          background-color: rgb(var(--rgb-core-medium-two)) !important;
        }
        .omni button.has-text-core-dark span {
          color: rgb(var(--rgb-core-dark));
        }
      `,
    ];
  }

  static register(name = 'om2-icon-menu') {
    return super.register(name, Om2IconMenu);
  }

  shouldUpdate(changed) {
    if (changed.has('options')) {
      // If given a list of values, transform it to a list of objects as we expect
      if (
        this.options &&
        this.options.length > 0 &&
        !(this.options[0] instanceof Object)
      ) {
        this.options = this.options.map(x => {
          const option = {};
          option[this.nameProperty] = x;
          return option;
        });
      }
    }
    return true;
  }

  async pollAndFixPosition() {
    // Only while the dropdown is active, detect positioning issues and fix it
    const trigger = this.shadowRoot.querySelector(
      'div.dropdown .dropdown-trigger'
    );
    const content = this.shadowRoot.querySelector(
      'div.dropdown .dropdown-content'
    );

    // Wait for dropdown to render itself
    await sleep(0);

    while (this.isActive) {
      const trigRect = trigger.getBoundingClientRect();
      let top = trigRect.bottom + 1;
      let maxHeight = Math.min(273, window.innerHeight - trigRect.bottom - 1);

      if (
        top + content.offsetHeight > window.innerHeight &&
        trigRect.top - 1 > window.innerHeight - top
      ) {
        // Not enough space to show below & there's more room above: "drop-up"
        top = trigRect.top - 1 - content.offsetHeight;
        maxHeight = Math.min(273, trigRect.top - 1);
      }

      const rightAlign =
        trigRect.right + 1 + content.offsetWidth > window.innerWidth;

      Object.assign(content.style, {
        top: `${top}px`,
        left: rightAlign
          ? `${trigRect.left - content.offsetWidth + trigRect.width}px`
          : `${trigRect.left}px`,
        maxHeight: `${maxHeight}px`,
      });

      // eslint-disable-next-line no-await-in-loop
      await sleep(200);
    }
  }

  async focusOut() {
    // Need to wait (sleep) for activeElement to be set
    await sleep(0);
    if (!this.shadowRoot.activeElement) {
      // Focus shifted outside of dropdown, we're no longer active
      this.setActive(false);
    }
  }

  async focusIn(e) {
    const target = e.target || e.srcElement;
    if (!this.isActive) {
      this.setActive(true);
    }
    if (this.isActive) {
      target.addEventListener('focusout', () => this.focusOut(), {
        once: true,
      });
    }
  }

  async setActive(to) {
    this.isActive = to;
    if (to) this.pollAndFixPosition();
  }

  render() {
    return html`<omni-style>
      <div
        class="dropdown ${this.isActive ? 'is-active' : ''}"
        tabindex="0"
        @focusin=${this.focusIn}
      >
        <div class="dropdown-trigger" slot="trigger">
          <omni-tooltip relateby="label" ?disabled=${!this.tooltip}>
            <button
              slot="invoker"
              part="invoker"
              class="button ${this.buttonClass} ${this.isActive
                ? 'is-active'
                : ''}"
              aria-haspopup="true"
              aria-controls="dropdown-menu"
              @click=${() => this.setActive(true)}
            >
              ${this.label ? html`<span>${this.label}</span>` : ''}
              ${this.iconId
                ? html`
                    <omni-icon
                      class="${this.iconClass}"
                      icon-id=${this.iconId}
                      slot="trigger-icon"
                      part="trigger-icon"
                    ></omni-icon>
                  `
                : ''}
            </button>
            <div slot="content">${this.tooltip}</div>
          </omni-tooltip>
        </div>
        <div class="dropdown-menu" id="dropdown-menu" role="menu" slot="menu">
          <div class="dropdown-content">
            ${this.options.map((option, i) => {
              const handler = () => {
                this.setActive(false);
                this.dispatchNewEvent('change', {
                  detail: { value: option, index: i },
                });
              };
              return html`
                <div
                  tabindex="0"
                  class="dropdown-item is-clickable"
                  @click=${handler}
                  @keyup=${e => ['Enter', ' '].includes(e.key) && handler()}
                >
                  ${option[this.nameProperty]}
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    </omni-style>`;
  }
}

Om2IconMenu.register();
